import { ReactNode } from 'react'
import { useAuth, usePermissions } from '@/hooks/useAuth'
import { Card, CardContent } from '@/components/ui/card'
import { Shield, AlertTriangle } from 'lucide-react'
import { getRoleLabel, type AppRole } from '../../../convex/lib/permissions'

interface PermissionGuardProps {
  permission?: string
  anyPermission?: string[]
  requiredRole?: AppRole
  children: ReactNode
  fallback?: ReactNode
  showError?: boolean
}

export function PermissionGuard({ 
  permission, 
  anyPermission,
  requiredRole, 
  children, 
  fallback,
  showError = true 
}: PermissionGuardProps) {
  const { profile } = useAuth()
  const { hasPermission } = usePermissions()

  // Check if user is authenticated
  if (!profile) {
    if (fallback) return <>{fallback}</>
    
    if (showError) {
      return (
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="p-8 text-center">
              <Shield className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h2 className="text-xl font-semibold mb-2">Accesso richiesto</h2>
              <p className="text-gray-600">Devi effettuare il login per accedere a questa pagina.</p>
            </CardContent>
          </Card>
        </div>
      )
    }
    
    return null
  }

  // Check role-based permission
  if (requiredRole && profile.ruolo !== requiredRole && profile.ruolo !== 'amministratore') {
    if (fallback) return <>{fallback}</>
    
    if (showError) {
      return (
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="p-8 text-center">
              <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-orange-400" />
              <h2 className="text-xl font-semibold mb-2">Accesso negato</h2>
              <p className="text-gray-600 mb-4">
                Non hai i permessi necessari per visualizzare questa pagina.
              </p>
              <div className="text-sm text-gray-500">
                <p>Ruolo richiesto: <span className="font-medium">{getRoleLabel(requiredRole)}</span></p>
                <p>Il tuo ruolo: <span className="font-medium">{getRoleLabel(profile.ruolo)}</span></p>
              </div>
            </CardContent>
          </Card>
        </div>
      )
    }
    
    return null
  }

  // Check permission-based access
  if (permission && !hasPermission(permission)) {
    if (fallback) return <>{fallback}</>
    
    if (showError) {
      return (
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="p-8 text-center">
              <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-orange-400" />
              <h2 className="text-xl font-semibold mb-2">Accesso negato</h2>
              <p className="text-gray-600 mb-4">
                Non hai i permessi necessari per visualizzare questa pagina.
              </p>
              <div className="text-sm text-gray-500">
                <p>Permesso richiesto: <span className="font-medium">{permission}</span></p>
                <p>Il tuo ruolo: <span className="font-medium">{getRoleLabel(profile.ruolo)}</span></p>
              </div>
            </CardContent>
          </Card>
        </div>
      )
    }
    
    return null
  }

  if (anyPermission && !anyPermission.some((candidate) => hasPermission(candidate))) {
    if (fallback) return <>{fallback}</>

    if (showError) {
      return (
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="p-8 text-center">
              <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-orange-400" />
              <h2 className="text-xl font-semibold mb-2">Accesso negato</h2>
              <p className="text-gray-600">
                Non hai i permessi necessari per visualizzare questa pagina.
              </p>
            </CardContent>
          </Card>
        </div>
      )
    }

    return null
  }

  // User has permission, render children
  return <>{children}</>
}

// Hook per uso condizionale
export function usePermissionGuard(permission?: string, requiredRole?: string) {
  const { profile } = useAuth()
  const { hasPermission } = usePermissions()

  if (!profile) return false
  
  if (requiredRole && profile.ruolo !== requiredRole && profile.ruolo !== 'amministratore') {
    return false
  }
  
  if (permission && !hasPermission(permission)) {
    return false
  }
  
  return true
}
